import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { trialAPI } from '@/services/api';

export interface TrialStatus {
  eligible: boolean;
  active: boolean;
  expired: boolean;
  used: boolean;
  session_id: number | null;
  document_id: number | null;
  messages_remaining: number;
  chat_count: number;
  hours_remaining: number;
  expires_at: string | null;
  kind?: 'legacy_no_card' | 'card_backed' | null;
  grandfathered?: boolean;
  analysis_limit: number;
  analysis_used: number;
  analysis_remaining: number;
  voice_minutes_limit?: number | null;
  voice_minutes_used?: number | null;
  voice_minutes_remaining?: number | null;
}

export interface TrialContextValue {
  trialStatus: TrialStatus | null;
  isLoading: boolean;
  refreshTrialStatus: () => Promise<void>;
  isTrialSession: (sessionId: number) => boolean;
}

const TrialContext = createContext<TrialContextValue | undefined>(undefined);

interface TrialProviderProps {
  children: ReactNode;
}

export function TrialProvider({ children }: TrialProviderProps) {
  const [trialStatus, setTrialStatus] = useState<TrialStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refreshTrialStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const status = await trialAPI.getStatus({ force: true });
      setTrialStatus(status);
    } catch {
      // Keep previous trialStatus — don't set to null
      // This ensures trial limits stay enforced even during network blips
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch trial status on mount
  useEffect(() => {
    refreshTrialStatus();
  }, [refreshTrialStatus]);

  // Returns true when the given session ID matches the active trial session
  const isTrialSession = useCallback(
    (sessionId: number): boolean => {
      return trialStatus?.session_id === sessionId;
    },
    [trialStatus],
  );

  const value: TrialContextValue = {
    trialStatus,
    isLoading,
    refreshTrialStatus,
    isTrialSession,
  };

  return <TrialContext.Provider value={value}>{children}</TrialContext.Provider>;
}

export function useTrialContext(): TrialContextValue {
  const ctx = useContext(TrialContext);
  if (!ctx) {
    throw new Error('useTrialContext must be used within a TrialProvider');
  }
  return ctx;
}

export default TrialContext;
